/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useBlacklistList.ts
 🗓️ Created: 2025-01-21
 🧩 Description:
    Custom hook for managing Blacklist entries list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchBlacklistEntries } from '../api';
import { BlacklistEntry } from '../types';

interface UseBlacklistListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
  initialColumns?: DataGridColumn<BlacklistEntry>[];
}

/**
 * Custom hook for Blacklist entries list
 * Implements Domain Contextual Filtering pattern
 */
export function useBlacklistList(options: UseBlacklistListOptions = {}) {
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchBlacklistEntries({
          ...params,
          domainId: effectiveDomainId,
        });
        return {
          data: result.data || [],
          pagination: result.pagination || {
            page: 1,
            limit: 25,
            total: 0,
            totalPages: 1,
          },
        };
      } catch (error) {
        return {
          data: [] as BlacklistEntry[],
          pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
        };
      }
    },
    [effectiveDomainId],
  );

  const gridResult = useDataGrid({
    fetchData,
    initialColumns: options.initialColumns,
    initialSortBy: 'value',
    initialSortOrder: 'asc',
    initialLimit: 25,
    moduleName: 'blacklist',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    ...gridResult,
    effectiveDomainId,
  };
}

